import { CommonModule, Location } from '@angular/common';
import { Component, computed, DestroyRef, inject, type OnDestroy, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	type AbstractControl,
	type FormArray,
	FormBuilder,
	type FormGroup,
	ReactiveFormsModule,
	type ValidationErrors,
	Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { COUNTRY_CODES, continentOf, countryName } from '@libs/dtos/merit/countries.catalog';
import type { CvExtractionDTO } from '@libs/dtos/merit/cv-extraction.dto';
import {
	AVAILABILITY,
	DEPARTMENTS,
	DRIVING_LICENCE,
	GCC_EXPERIENCE,
	GCC_LOCATIONS,
	LANGUAGE_LEVELS,
	LANGUAGES,
	LUXURY_LEVEL,
	MANAGEMENT_EXPERIENCE,
	offeredSalaryExpectation,
	PROPERTY_TYPES,
	SALARY_EXPECTATIONS,
	SYSTEMS,
	TARGET_POSITIONS,
	VISA_OPTIONS,
} from '@libs/dtos/merit/merit.catalogs';
import {
	CV_EXTENSIONS,
	CV_MAX_BYTES,
	CV_MIME_TYPES,
	EDUCATION_LEVELS,
	ONBOARDING_SCREEN_STEPS,
	ONBOARDING_SCREENS,
	ONBOARDING_STEPS,
	type OnboardingScreen,
	type OnboardingStateDTO,
	type OnboardingStep,
	PHOTO_MAX_BYTES,
	PHOTO_MIME_TYPES,
	screenOfStep,
	VIDEO_MAX_BYTES,
	VIDEO_MAX_SECONDS,
	VIDEO_MIME_TYPES,
	VIDEO_MIN_SECONDS,
} from '@libs/dtos/merit/onboarding.dto';
import { MATRIX_SPECIALIZATIONS } from '@libs/dtos/merit/vacancy-rules.catalogs';
import { type CatalogType, MeritConfigService } from '@services/merit/merit-config.service';
import { MeritI18nService } from '@services/merit/merit-i18n.service';
import {
	type OnboardingGateway,
	OnboardingService,
	type OnboardingUploadKind,
} from '@services/merit/onboarding.service';
import { PortalService } from '@services/merit/portal.service';
import { ChipsInputComponent } from '@shared/components/chips-input/chips-input.component';
import { CountrySelectComponent } from '@shared/components/country-select/country-select.component';
import { MultiselectComponent } from '@shared/components/multiselect/multiselect.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { LucideModule } from '@shared/modules/lucide.module';
import { videoDurationErrorMessage } from '@shared/utils/video-upload-error.util';
import { catchError, finalize, map, type Observable, of, type Subscription, switchMap, tap } from 'rxjs';
import { type AcceptedUpload, OnboardingDropzoneComponent } from './onboarding-dropzone/onboarding-dropzone.component';
import { OnboardingReviewComponent } from './onboarding-review/onboarding-review.component';

type ExperienceEntry = OnboardingStateDTO['experience'][number];
type EducationEntry = OnboardingStateDTO['education'][number];
type LanguageEntry = OnboardingStateDTO['languages'][number];

/**
 * Relocation answers. They used to live in `merit.catalogs` and were dropped
 * when the intake form moved to the DB catalogs; relocation is not one of the
 * seven master catalogs, so the three canonical answers live here (their
 * Spanish labels are already in `merit.options`).
 */
const RELOCATION = [
	'Open to Relocate within UAE',
	'Open to relocate within UAE, and GCC (Oman, Qatar, Bahrein, Kuwait and Saudi Arabia)',
	'Not Open to Relocate',
] as const;

/** Seed values as fallback while the catalog request is in flight or fails. */
const FALLBACK_SPECIALIZATIONS = MATRIX_SPECIALIZATIONS.flatMap((group) => [...group.values]);

/** Catalogs this wizard reads from M00. */
type WizardCatalog =
	| 'positions'
	| 'departments'
	| 'languages'
	| 'cities'
	| 'property_types'
	| 'luxury_levels'
	| 'systems'
	| 'specializations';

/**
 * M02 · "Completa tu perfil" — the ten-step onboarding wizard.
 *
 * ONE component for both modes (M02 §5, and the v2 lesson in docs/merit/PLAN.md:
 * never duplicate the form). What changes between the public URL and the
 * verified candidate is only the gateway it talks to — resolved from the
 * presence of a `:token` route param — plus the brand chrome the public page
 * wears instead of the portal sidebar (§6).
 */
/** An array control with at least one item; `Validators.required` accepts `[]`. */
const atLeastOne = (control: AbstractControl): ValidationErrors | null =>
	Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };

/**
 * CEFR → the four-word scale the step row keeps (23/09). The screen asks CEFR
 * because the four-word scale folded B1 and B2 together, and a B2 speaker stored
 * as B1 failed the English rule on the spot.
 *
 * C2 is «native» here and only here: the platform labels C2 «Native», so a
 * person who picks it said so. An import that finds a C2 does NOT make anyone a
 * native speaker (`proficiencyOf` in the backend).
 */
const PROFICIENCY_OF_CEFR: Record<string, LanguageEntry['level']> = {
	A2: 'basic',
	B1: 'intermediate',
	B2: 'intermediate',
	C1: 'fluent',
	C2: 'native',
};

/** The public signup before its draft exists: nothing on the server yet. */
function emptySignupState(): OnboardingStateDTO {
	return {
		candidateId: 0,
		status: 'draft',
		currentStep: 0,
		completedSteps: [],
		canSubmit: false,
		blockingReasons: [],
		personal: null,
		experience: [],
		managementExperience: null,
		education: [],
		languages: [],
		legal: null,
		availability: null,
		preferences: null,
		photo: null,
		cv: null,
		video: null,
		expiresAt: null,
		underReview: false,
	};
}

/** «linkedin.com/in/ana» is what people type; the contract wants a URL. */
function withScheme(value: string | null | undefined): string | null {
	const text = value?.trim();
	if (!text) return null;
	return /^https?:\/\//i.test(text) ? text : `https://${text}`;
}

@Component({
	selector: 'app-onboarding',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		RouterLink,
		TranslocoModule,
		LucideModule,
		SelectComponent,
		CountrySelectComponent,
		MultiselectComponent,
		ChipsInputComponent,
		SpinnerComponent,
		OnboardingDropzoneComponent,
		OnboardingReviewComponent,
	],
	templateUrl: './onboarding.component.html',
	styleUrl: './onboarding.component.scss',
})
export class OnboardingComponent implements OnInit, OnDestroy {
	private readonly fb = inject(FormBuilder);
	private readonly route = inject(ActivatedRoute);
	private readonly onboarding = inject(OnboardingService);
	private readonly portal = inject(PortalService);
	private readonly config = inject(MeritConfigService);
	private readonly transloco = inject(TranslocoService);
	private readonly location = inject(Location);

	private readonly destroyRef = inject(DestroyRef);
	/** Los catálogos no cargaron: sin ellos el asistente no se puede rellenar. */
	readonly catalogsFailed = signal(false);
	readonly meritI18n = inject(MeritI18nService);

	// ---- Mode ---------------------------------------------------------------

	/** Token from `/onboarding/:token`; null in the authenticated portal. */
	private token: string | null = null;
	private gateway!: OnboardingGateway;

	readonly isPublic = signal(false);

	/**
	 * `/candidate-signup` before the draft exists (23/09): the Documents block
	 * asks for the email and the privacy box, and the first file creates the
	 * draft. Until then nothing is on the server and the other steps are locked
	 * — there is nowhere to save them.
	 */
	readonly signupPending = signal(false);
	readonly starting = signal(false);
	readonly startError = signal<string | null>(null);
	/** The 409 for an address that is already registered: the message carries a «Sign in» link. */
	readonly startEmailTaken = signal(false);
	readonly startTried = signal(false);

	readonly startForm = this.fb.nonNullable.group({
		email: ['', [Validators.required, Validators.email]],
		privacy: [false, Validators.requiredTrue],
		// Honeypot: hidden on screen, so only a bot fills it in.
		website: [''],
	});

	// ---- Screen state -------------------------------------------------------

	/**
	 * What the rail shows (23/09): the three files in one «Documents» block, then
	 * the seven data steps. The storage steps underneath do not change — see
	 * `ONBOARDING_SCREENS`.
	 */
	readonly steps = ONBOARDING_SCREENS;
	readonly loading = signal(true);
	readonly loadError = signal<string | null>(null);
	readonly state = signal<OnboardingStateDTO | null>(null);
	readonly index = signal(0);
	/** Step asked for through `?step=`, honoured once the state has loaded. */
	private requestedStep: number | null = null;
	readonly view = signal<'wizard' | 'review' | 'done'>('wizard');
	/** The submit opened a migrated candidate's portal (10/09): the end screen says so. */
	readonly unlocked = signal(false);
	/** A migrated candidate's changes to the imported data are with the team (4.1, 11/09). */
	readonly reviewing = signal(false);
	readonly saving = signal(false);
	readonly submitting = signal(false);
	readonly stepError = signal<string | null>(null);
	readonly submitError = signal<string | null>(null);
	readonly uploadError = signal<string | null>(null);
	readonly uploadingKind = signal<OnboardingUploadKind | null>(null);
	/** Field errors only surface once the candidate tried to move on. */
	readonly triedNext = signal(false);

	readonly step = computed<OnboardingScreen>(() => this.steps[this.index()]);
	/**
	 * Screens done. Documents counts once the CV and the photo are in: the video
	 * is optional, and waiting for it would leave the tick off for most people.
	 */
	readonly completed = computed(() => {
		const done = new Set<string>(this.state()?.completedSteps ?? []);
		return new Set<OnboardingScreen>(
			this.steps.filter((screen) =>
				screen === 'documents'
					? done.has('cv') && done.has('photo')
					: ONBOARDING_SCREEN_STEPS[screen].every((step) => done.has(step)),
			),
		);
	});
	readonly progress = computed(() => Math.round((this.completed().size / this.steps.length) * 100));
	/**
	 * Screens the server says still block the submit (13/09, client): the i18n
	 * key of each reason names its storage step, and the screen that shows it
	 * gets the red dot once the person has seen the review screen, so «something
	 * is missing» points at where.
	 */
	readonly missingSteps = computed(() => {
		const keys = this.state()?.blockingReasons ?? [];
		const names = keys.map((key) => key.replace('merit.onboarding.blocking.', ''));
		return new Set(
			names
				.filter((name): name is OnboardingStep => (ONBOARDING_STEPS as readonly string[]).includes(name))
				.map((name) => screenOfStep(name)),
		);
	});
	readonly reviewVisited = signal(false);
	readonly showMissing = computed(() => this.reviewVisited() && this.missingSteps().size > 0);

	// File limits straight from the contract, so the copy and the gate agree.
	readonly photoMime = PHOTO_MIME_TYPES;
	readonly photoMaxBytes = PHOTO_MAX_BYTES;
	readonly cvMime = CV_MIME_TYPES;
	readonly cvExtensions = CV_EXTENSIONS;
	readonly cvMaxBytes = CV_MAX_BYTES;
	readonly videoMime = VIDEO_MIME_TYPES;
	readonly videoMaxBytes = VIDEO_MAX_BYTES;
	readonly videoMinSeconds = VIDEO_MIN_SECONDS;
	readonly videoMaxSeconds = VIDEO_MAX_SECONDS;

	/** Label of the primary button: save, move on, or open the review screen. */
	readonly nextLabel = computed(() => {
		if (this.saving()) return 'merit.onboarding.actions.saving';
		return this.index() === this.steps.length - 1
			? 'merit.onboarding.actions.review'
			: 'merit.onboarding.actions.next';
	});

	/** Two-digit step number ("01"… "10"). */
	pad(n: number): string {
		return n < 10 ? `0${n}` : String(n);
	}

	private langSub?: Subscription;

	// ---- Nacionalidad -------------------------------------------------------

	/** El país que traía el borrador al abrirlo. Null si aún no tenía. */
	private storedCountryCode: string | null = null;

	/**
	 * El texto libre `nationality` que se envía junto al código.
	 *
	 * El campo ya no se elige, pero **no desaparece**: es lo que declaró la
	 * persona y el código es una normalización nuestra — sin el original no hay
	 * forma de revisar una correspondencia dudosa (ver la migración
	 * `candidate_country_code`). Por eso solo se reescribe cuando el país CAMBIA:
	 * corregirlo desde la cascada ES la revisión.
	 *
	 * En español porque así son las etiquetas del catálogo `nationalities`, que es
	 * lo que este campo guardaba antes.
	 */
	private nationalityToSend(countryCode: string, stored: string): string {
		if (!countryCode) return stored;
		if (countryCode === this.storedCountryCode && stored) return stored;
		return countryName(countryCode, 'es');
	}

	/** Nombre de país (es/en) → código, sin acentos ni mayúsculas. */
	private static countryIndex: Map<string, string> | null = null;

	/**
	 * El código que corresponde EXACTAMENTE a un nombre de país escrito en español
	 * o en inglés. Null si no casa.
	 *
	 * Deliberadamente exacto y no aproximado: esto solo alimenta una *sugerencia*
	 * del CV que la persona confirma antes de guardar, y un país inventado saldría
	 * después en los informes por continente como si fuera un dato comprobado. Un
	 * gentilicio («turkish») no casa, y eso es correcto: mejor sin sugerencia que
	 * con una adivinada.
	 */
	private static countryCodeOf(value: string | null | undefined): string | null {
		if (!value) return null;
		if (!OnboardingComponent.countryIndex) {
			const index = new Map<string, string>();
			for (const code of COUNTRY_CODES) {
				for (const locale of ['es', 'en']) {
					index.set(OnboardingComponent.normalize(countryName(code, locale)), code);
				}
			}
			OnboardingComponent.countryIndex = index;
		}
		return OnboardingComponent.countryIndex.get(OnboardingComponent.normalize(value)) ?? null;
	}

	/**
	 * País de UN empleo. Un código ISO que el catálogo conoce se guarda; un nombre
	 * exacto (es/en) se resuelve; lo demás se deja vacío para que la persona lo elija.
	 * No se adivina: nacionalidad, idioma o el nombre del hotel no son el país del puesto.
	 */
	private static jobCountryOf(value: string | null | undefined): string {
		if (!value) return '';
		const trimmed = value.trim();
		if (/^[A-Za-z]{2}$/.test(trimmed) && continentOf(trimmed)) return trimmed.toUpperCase();
		return OnboardingComponent.countryCodeOf(trimmed) ?? '';
	}

	private static normalize(value: string): string {
		return value
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.trim()
			.toLowerCase();
	}

	// ---- Form ---------------------------------------------------------------

	readonly form = this.fb.group({
		personal: this.fb.group({
			fullName: ['', [Validators.required, Validators.minLength(2)]],
			email: ['', [Validators.required, Validators.email]],
			phone: ['', [Validators.required, Validators.minLength(5)]],
			birthDate: ['', Validators.required],
			/**
			 * Texto libre histórico. Sigue viajando —es lo que declaró la persona—
			 * pero ya no se elige: lo deriva el país, así que no lleva validador.
			 * Ver `nationalityToSend()`.
			 */
			nationality: [''],
			/** Nacionalidad de verdad: cascada continente → país, código ISO. */
			countryCode: ['', Validators.required],
			city: ['', Validators.required],
			// «Otro» en la lista de ciudades: aquí se escribe cuál (04/09).
			cityOther: [''],
			// Obligatorio desde el 23/09: el asistente es el alta pública, y el
			// formulario corto al que sustituye ya lo pedía.
			linkedinUrl: ['', Validators.required],
			// Sin validador: es opcional a propósito y no aportarla no penaliza.
			// Obligatoria desde el 02/09 (C-P1-10) — informativa, pero requerida.
			heightCm: [null as number | null, [Validators.required, Validators.min(100), Validators.max(250)]],
		}),
		experience: this.fb.array<FormGroup>([]),
		// Saved with the experience step (23/09): it is about the career, and the
		// K-Score needs it now that the short signup form is gone.
		experienceInfo: this.fb.group({
			managementExperience: ['', Validators.required],
		}),
		education: this.fb.array<FormGroup>([]),
		languages: this.fb.array<FormGroup>([]),
		legal: this.fb.group({
			visaStatus: ['', Validators.required],
			emiratesId: [''],
			needsSponsor: [false],
			currentEmployment: [''],
			drivingLicence: ['', Validators.required],
		}),
		availability: this.fb.group({
			availability: ['', Validators.required],
			relocation: ['', Validators.required],
			gccExperience: ['', Validators.required],
			// The bracket the scoring and the matching compare (23/09). The AED
			// number below stays only to send back what an old row held.
			salaryExpectation: ['', Validators.required],
			// «Ubicaciones de interés» (13/09, clienta): al menos una. `required` da
			// por bueno un array vacío, así que va con su propio validador.
			locationTargeting: [[] as string[], atLeastOne],
			salaryExpectationAed: [null as number | null],
			// Días de preaviso (MD-Vacante §5.5): input del matching, opcional.
			noticePeriodDays: [null as number | null],
		}),
		preferences: this.fb.group({
			// Up to three target positions (M02 §2); at least one to move on.
			targetPositions: [[] as string[], [Validators.required, Validators.maxLength(3)]],
			// «Other» con su texto (03/09 · 31).
			targetPositionOther: [null as string | null],
			propertyTypes: [[] as string[]],
			// MD-Vacante §6: años EN el puesto objetivo y sistemas que maneja.
			yearsInTargetRole: [null as number | null],
			systems: [[] as string[]],
			// Matrix §4: specializations (cuisines, sections, PMS areas…).
			specializations: [[] as string[]],
			// Habilidades en texto libre. Están AQUÍ y no solo en el alta porque
			// `savePreferences` reescribe la fila entera: si el asistente no las
			// mandara, terminar el paso borraría lo que la persona escribió al
			// darse de alta.
			skills: [[] as string[]],
		}),
	});

	get experience(): FormArray<FormGroup> {
		return this.form.controls.experience;
	}

	get education(): FormArray<FormGroup> {
		return this.form.controls.education;
	}

	get languages(): FormArray<FormGroup> {
		return this.form.controls.languages;
	}

	// ---- Lifecycle ----------------------------------------------------------

	ngOnInit(): void {
		this.token = this.route.snapshot.paramMap.get('token');

		// `?step=experience` opens the wizard on that step. "Mi perfil" edits one
		// section at a time, and landing on step 1 to walk to step 3 was the whole
		// reason the profile's Editar links felt like a detour.
		//
		// Kept in a field, not applied here: load() below resumes at the saved
		// `currentStep` and would overwrite it a moment later.
		// A storage step name (`?step=photo`, what older links carry) opens the
		// screen that shows it.
		const requested = this.route.snapshot.queryParamMap.get('step');
		const screen = !requested
			? null
			: (this.steps as readonly string[]).includes(requested)
				? (requested as OnboardingScreen)
				: (ONBOARDING_STEPS as readonly string[]).includes(requested)
					? screenOfStep(requested as OnboardingStep)
					: null;
		this.requestedStep = screen ? this.steps.indexOf(screen) : null;
		const signup = this.token == null && this.route.snapshot.data['signup'] === true;
		this.signupPending.set(signup);
		this.isPublic.set(this.token != null || signup);
		this.gateway = this.token ? this.onboarding.forToken(this.token) : this.onboarding.forCurrentUser();

		this.langSub = this.transloco.langChanges$.subscribe(() => this.rebuildOptions());
		this.rebuildOptions();
		this.loadCatalogs();

		if (signup) {
			// Nothing to load: the draft is born with the first file.
			this.transloco.setActiveLang('en');
			this.transloco.load('en').subscribe({
				next: () => this.openEmptySignup(),
				error: () => this.openEmptySignup(),
			});
			return;
		}

		if (this.isPublic()) {
			// Same rule as the public completion page: candidate-facing pages reached
			// by token are shown in English (international talent), whatever the
			// app's default locale is. Wait for the bundle so imperatively-read
			// labels resolve in English too.
			this.transloco.setActiveLang('en');
			this.transloco.load('en').subscribe({ next: () => this.load(), error: () => this.load() });
			return;
		}
		this.load();
	}

	ngOnDestroy(): void {
		this.langSub?.unsubscribe();
		if (this.cvPoll) clearTimeout(this.cvPoll);
	}

	private load(): void {
		this.loading.set(true);
		this.loadError.set(null);
		this.gateway.load().subscribe({
			next: (state) => {
				this.applyState(state, true);
				this.reapplyCvProposal(state);
				// An explicit ?step wins over resuming: the candidate said which
				// section they came to edit. Otherwise, resume exactly where they
				// left off (M02 test case 2).
				this.index.set(this.requestedStep ?? this.resumeScreen(state.currentStep));
				this.loading.set(false);
			},
			error: (err) => {
				this.loading.set(false);
				const invalid = err?.status === 404 || err?.status === 403 || err?.status === 410;
				this.loadError.set(
					this.t(invalid ? 'merit.onboarding.errors.invalidToken' : 'merit.onboarding.errors.load'),
				);
			},
		});
	}

	/**
	 * The screen to resume on, from the stored pointer over the STORAGE steps.
	 * The pointer lands on `video` once preferences are saved (it is the last
	 * storage step), but on screen the video lives in Documents, at the top:
	 * whoever got that far resumes on the last data screen instead.
	 */
	private resumeScreen(currentStep: number): number {
		const step = ONBOARDING_STEPS[Math.min(Math.max(currentStep, 0), ONBOARDING_STEPS.length - 1)];
		if (step === 'video') return this.steps.length - 1;
		return this.steps.indexOf(screenOfStep(step));
	}

	private openEmptySignup(): void {
		this.state.set(emptySignupState());
		this.index.set(0);
		this.loading.set(false);
	}

	retry(): void {
		// También los catálogos: si lo que falló fue eso, reintentar solo el
		// estado dejaría el aviso puesto para siempre.
		this.catalogsFailed.set(false);
		this.loadCatalogs();
		if (this.signupPending()) {
			this.openEmptySignup();
			return;
		}
		this.load();
	}

	// ---- Public signup (23/09) ----------------------------------------------

	/**
	 * Makes sure the signup draft exists before anything is saved into it.
	 *
	 * Called by the first file and by «Save and continue» on Documents. With the
	 * email and the privacy box right, it creates the draft, switches the page to
	 * token mode and loads the state — the email the person typed comes back as
	 * the personal step's prefill. The URL becomes `/onboarding/<token>`, so a
	 * reload resumes the same draft instead of starting a new one.
	 */
	private ensureStarted(): Observable<boolean> {
		if (!this.signupPending()) return of(true);
		this.startTried.set(true);
		this.startError.set(null);
		this.startEmailTaken.set(false);
		if (this.startForm.invalid) {
			this.startForm.markAllAsTouched();
			this.startError.set(this.t('merit.onboarding.signup.startFirst'));
			return of(false);
		}
		const { email, website } = this.startForm.getRawValue();
		this.starting.set(true);
		return this.onboarding.startSignup({ email: email.trim(), privacy: true, website: website || undefined }).pipe(
			switchMap(({ token }) => {
				// The server took it for a bot: nothing to go on with.
				if (!token) return of(false);
				this.token = token;
				this.gateway = this.onboarding.forToken(token);
				this.signupPending.set(false);
				this.location.replaceState(`/onboarding/${token}`);
				return this.gateway.load().pipe(
					tap((state) => this.applyState(state, true)),
					map(() => true),
				);
			}),
			catchError((err) => {
				const code = (err as { error?: { message?: unknown } })?.error?.message;
				if (code === 'SIGNUP_ALREADY_STARTED') {
					this.startError.set(this.t('merit.onboarding.signup.alreadyStarted'));
				} else if (code === 'EMAIL_ALREADY_EXISTS') {
					this.startEmailTaken.set(true);
					this.startError.set(this.t('merit.onboarding.signup.emailExists'));
				} else {
					this.startError.set(this.t('merit.onboarding.signup.error'));
				}
				return of(false);
			}),
			finalize(() => this.starting.set(false)),
		);
	}

	showStartErr(control: 'email' | 'privacy'): boolean {
		return this.startTried() && this.startForm.controls[control].invalid;
	}

	// ---- Catalogs (M00) -----------------------------------------------------

	cityOptions: { label: string; value: string }[] = [];
	positionOptions: { label: string; value: string }[] = [];
	departmentOptions: { label: string; value: string }[] = [];
	luxuryOptions: { label: string; value: string }[] = [];
	languageOptions: { label: string; value: string }[] = [];
	propertyTypeOptions: { label: string; value: string }[] = [];
	visaOptions: { label: string; value: string }[] = [];
	availabilityOptions: { label: string; value: string }[] = [];
	relocationOptions: { label: string; value: string }[] = [];
	locationOptions: { label: string; value: string }[] = [];
	gccOptions: { label: string; value: string }[] = [];
	educationLevelOptions: { label: string; value: string }[] = [];
	/** CEFR plus «native» (23/09). */
	languageLevelOptions: { label: string; value: string }[] = [];
	managementOptions: { label: string; value: string }[] = [];
	drivingOptions: { label: string; value: string }[] = [];
	salaryOptions: { label: string; value: string }[] = [];
	systemsOptions: { label: string; value: string }[] = [];
	specializationsOptions: { label: string; value: string }[] = [];

	/** Active catalog values once `GET /config/catalogs` lands; null until then. */
	private catalogs: Partial<Record<WizardCatalog, string[]>> | null = null;

	/**
	 * Only the *active* values of each master catalog (M02 test case 7). If the
	 * call fails the wizard keeps the code constants — the same values the
	 * catalogs were seeded from — so it stays usable.
	 */
	private loadCatalogs(): void {
		this.config.listAllCatalogs().subscribe({
			next: (catalogs) => {
				const labels = (type: CatalogType) => (catalogs?.[type] ?? []).map((item) => item.label);
				this.catalogs = {
					positions: labels('positions'),
					departments: labels('departments'),
					languages: labels('languages'),
					cities: labels('cities'),
					property_types: labels('property_types'),
					luxury_levels: labels('luxury_levels'),
					systems: labels('systems'),
					specializations: labels('specializations'),
				};
				this.rebuildOptions();
			},
			// Sin catálogos, TODOS los desplegables del asistente salen vacíos: el
			// candidato ve un formulario que no puede rellenar y ni una pista de
			// por qué. `catalogsFailed` es lo que la plantilla usa para decirlo.
			error: () => this.catalogsFailed.set(true),
		});
	}

	private catalogValues(type: WizardCatalog, fallback: readonly string[] = []): readonly string[] {
		const values = this.catalogs?.[type];
		return values?.length ? values : fallback;
	}

	/**
	 * (Re)builds every dropdown. Re-run when the catalogs land, after the prefill
	 * and on a locale change: values already stored on the profile are merged back
	 * in (`withStored`) so a deactivated or renamed catalog value stays selected
	 * instead of silently blanking the field.
	 */
	private rebuildOptions(): void {
		const experience = this.experience.getRawValue() as ExperienceEntry[];
		const languages = this.languages.getRawValue() as LanguageEntry[];
		const preferences = this.form.controls.preferences.getRawValue();
		const personal = this.form.controls.personal.getRawValue();
		const availability = this.form.controls.availability.getRawValue();
		const i18n = this.meritI18n;

		this.cityOptions = i18n.options(i18n.withStored(this.catalogValues('cities', GCC_LOCATIONS), personal.city));
		// «Other» vuelve a los puestos objetivo (clienta, 03/09 · 31): con su
		// texto libre, y Kairos lo normaliza después desde el panel.
		// El texto de «Other» es obligatorio solo cuando se elige «Other».
		const positionsControl = this.form.controls.preferences.controls.targetPositions;
		const otherControl = this.form.controls.preferences.controls.targetPositionOther;
		const syncOtherValidator = (values: string[] | null) => {
			otherControl.setValidators(values?.includes('Other') ? [Validators.required, Validators.minLength(2)] : []);
			otherControl.updateValueAndValidity({ emitEvent: false });
		};
		syncOtherValidator(positionsControl.value);
		positionsControl.valueChanges.subscribe(syncOtherValidator);
		this.positionOptions = i18n.options(
			i18n.withStored(
				this.catalogValues('positions', TARGET_POSITIONS),
				preferences.targetPositions,
				experience.map((e) => e.position),
			),
		);
		this.departmentOptions = i18n.options(
			i18n.withStored(
				this.catalogValues('departments', DEPARTMENTS),
				experience.map((e) => e.department),
			),
		);
		this.luxuryOptions = i18n.options(
			i18n.withStored(
				this.catalogValues('luxury_levels', LUXURY_LEVEL),
				experience.map((e) => e.luxuryLevel),
			),
		);
		this.languageOptions = i18n.languageOptions(
			i18n.withStored(
				this.catalogValues('languages', LANGUAGES),
				languages.map((l) => l.language),
			),
		);
		this.propertyTypeOptions = i18n.options(
			i18n.withStored(this.catalogValues('property_types', PROPERTY_TYPES), preferences.propertyTypes),
		);
		this.visaOptions = i18n.options(VISA_OPTIONS);
		this.systemsOptions = i18n.options(
			i18n.withStored(this.catalogValues('systems', SYSTEMS), preferences.systems),
		);
		// Flat + filterable, not grouped: the matrix wants CONTEXTUAL lists per
		// area, but no positions→area mapping exists yet — the filter box is the
		// honest middle until that mapping is a product decision.
		this.specializationsOptions = i18n.options(
			i18n.withStored(
				this.catalogValues('specializations', FALLBACK_SPECIALIZATIONS),
				preferences.specializations,
			),
		);
		this.availabilityOptions = i18n.options(AVAILABILITY);
		this.relocationOptions = i18n.options(RELOCATION);
		this.locationOptions = i18n.options(
			i18n.withStored(this.catalogValues('cities', GCC_LOCATIONS), ...(availability.locationTargeting ?? [])),
		);
		this.gccOptions = i18n.options(GCC_EXPERIENCE);
		// These two are enums of the contract, not catalogs: their labels are i18n.
		this.educationLevelOptions = EDUCATION_LEVELS.map((value) => ({
			label: this.t(`merit.onboarding.educationLevels.${value}`),
			value,
		}));
		this.languageLevelOptions = i18n.levelOptions(LANGUAGE_LEVELS);
		this.managementOptions = i18n.options(MANAGEMENT_EXPERIENCE);
		this.drivingOptions = i18n.options(DRIVING_LICENCE);
		this.salaryOptions = i18n.options(SALARY_EXPECTATIONS);
	}

	// ---- Repeatable entries -------------------------------------------------

	private experienceGroup(entry?: Partial<ExperienceEntry>): FormGroup {
		return this.fb.group({
			position: [entry?.position ?? '', Validators.required],
			department: [entry?.department ?? '', Validators.required],
			employerProperty: [entry?.employerProperty ?? '', Validators.required],
			luxuryLevel: [entry?.luxuryLevel ?? '', Validators.required],
			countryCode: [entry?.countryCode ?? '', Validators.required],
			fromDate: [entry?.fromDate ?? '', Validators.required],
			// Empty = still working there (the contract's null `toDate`).
			toDate: [entry?.toDate ?? ''],
			description: [entry?.description ?? ''],
		});
	}

	private educationGroup(entry?: Partial<EducationEntry>): FormGroup {
		return this.fb.group({
			level: [entry?.level ?? '', Validators.required],
			title: [entry?.title ?? '', Validators.required],
			institution: [entry?.institution ?? '', Validators.required],
			year: [entry?.year ?? null, [Validators.required, Validators.min(1950), Validators.max(2100)]],
			isCertification: [entry?.isCertification ?? false],
		});
	}

	/**
	 * One language. The screen asks ONE level, `cefr`; the four-word `level`
	 * the step row keeps is derived from it on save (`stepPayload`). A row saved
	 * as «native» before CEFR was asked opens on C2, which the platform labels so.
	 */
	private languageGroup(entry?: Partial<LanguageEntry>): FormGroup {
		const picked = entry?.cefr ?? (entry?.level === 'native' ? 'C2' : '');
		return this.fb.group({
			language: [entry?.language ?? '', Validators.required],
			cefr: [picked, Validators.required],
			percent: [entry?.percent ?? null, [Validators.min(0), Validators.max(100)]],
		});
	}

	addExperience(): void {
		this.experience.push(this.experienceGroup());
	}

	addEducation(): void {
		this.education.push(this.educationGroup());
	}

	addLanguage(): void {
		this.languages.push(this.languageGroup());
	}

	removeEntry(array: FormArray<FormGroup>, i: number): void {
		array.removeAt(i);
	}

	// ---- Navigation ---------------------------------------------------------

	/** Documents carries the three files, not a JSON body. */
	isUploadStep(step: OnboardingScreen): boolean {
		return step === 'documents';
	}

	/** Whether the current step satisfies its mandatory fields. */
	readonly currentStepValid = computed(() => this.stepIsValid(this.step()));

	private stepIsValid(step: OnboardingScreen): boolean {
		switch (step) {
			case 'personal':
				return this.form.controls.personal.valid;
			// At least one entry each (23/09): the server refuses an empty list now
			// too, so the step says it here instead of failing on save.
			case 'experience':
				return this.experience.valid && this.experience.length > 0 && this.form.controls.experienceInfo.valid;
			case 'education':
				return this.education.valid && this.education.length > 0;
			case 'languages':
				return this.languages.valid && this.languages.length > 0;
			case 'legal':
				return this.form.controls.legal.valid;
			case 'availability':
				return this.form.controls.availability.valid;
			case 'preferences':
				return this.form.controls.preferences.valid;
			default:
				// Files never block navigation: they can be added later and
				// `canSubmit` is what gates the actual submit. The one exception is
				// the signup that has not started — see `next()`.
				return true;
		}
	}

	private markStepTouched(step: OnboardingScreen): void {
		switch (step) {
			case 'personal':
				this.form.controls.personal.markAllAsTouched();
				break;
			case 'experience':
				this.experience.markAllAsTouched();
				this.form.controls.experienceInfo.markAllAsTouched();
				break;
			case 'education':
				this.education.markAllAsTouched();
				break;
			case 'languages':
				this.languages.markAllAsTouched();
				break;
			case 'legal':
				this.form.controls.legal.markAllAsTouched();
				break;
			case 'availability':
				this.form.controls.availability.markAllAsTouched();
				break;
			case 'preferences':
				this.form.controls.preferences.markAllAsTouched();
				break;
			default:
				break;
		}
	}

	back(): void {
		if (this.view() === 'review') {
			this.view.set('wizard');
			this.index.set(this.steps.length - 1);
			return;
		}
		if (this.index() > 0) this.goTo(this.index() - 1);
	}

	/**
	 * Saves the current step and moves on. The last step opens the review screen.
	 * An incomplete step stops here with its fields marked (M02 §3).
	 */
	next(): void {
		const step = this.step();
		this.triedNext.set(true);
		// The signup that has not started: «Save and continue» on Documents is
		// what creates the draft, even before any file (they can come later).
		if (this.signupPending()) {
			this.ensureStarted().subscribe((ok) => ok && this.moveTo(this.index() + 1));
			return;
		}
		if (!this.stepIsValid(step)) {
			this.markStepTouched(step);
			this.stepError.set(this.t('merit.onboarding.errors.completeFields'));
			return;
		}
		const last = this.index() === this.steps.length - 1;
		this.persist(step, () => (last ? this.view.set('review') : this.moveTo(this.index() + 1)));
	}

	/** Jump to any step from the rail (or from the review screen). */
	goTo(index: number): void {
		// Before the draft exists there is nowhere to save the other steps.
		if (this.signupPending() && index !== 0) {
			this.startError.set(this.t('merit.onboarding.signup.locked'));
			return;
		}
		const target = Math.min(Math.max(index, 0), this.steps.length - 1);
		const step = this.step();
		// Save silently if what is on screen is complete; never block the jump.
		if (this.view() === 'wizard' && this.stepIsValid(step) && !this.isUploadStep(step)) {
			this.persist(step, () => this.moveTo(target));
			return;
		}
		this.moveTo(target);
	}

	private moveTo(index: number): void {
		this.index.set(index);
		this.view.set('wizard');
		this.triedNext.set(false);
		this.stepError.set(null);
		this.uploadError.set(null);
		if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	openReview(): void {
		if (this.signupPending()) {
			this.startError.set(this.t('merit.onboarding.signup.locked'));
			return;
		}
		this.view.set('review');
		this.reviewVisited.set(true);
		this.submitError.set(null);
		if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	// ---- Persistence --------------------------------------------------------

	private persist(step: OnboardingScreen, done: () => void): void {
		if (this.isUploadStep(step)) {
			done();
			return;
		}
		this.saving.set(true);
		this.stepError.set(null);
		// Every screen but Documents is exactly one storage step of the same name.
		this.gateway.saveStep(step as OnboardingStep, this.stepPayload(step)).subscribe({
			next: (state) => {
				this.saving.set(false);
				this.applyState(state, false);
				// Guardar el paso ES la verificación: lo que la IA sugirió ya ha
				// pasado por delante de la persona, así que la etiqueta se retira.
				this.clearSuggestedFor(step);
				done();
			},
			error: (err) => {
				this.saving.set(false);
				this.stepError.set(this.apiError(err, 'merit.onboarding.errors.save'));
			},
		});
	}

	/** Retira las etiquetas "sugerido del CV" de un paso ya guardado. */
	private clearSuggestedFor(step: OnboardingScreen): void {
		const marks = this.cvSuggested();
		if (!marks.size) return;
		const left = new Set([...marks].filter((path) => !path.startsWith(step)));
		if (left.size !== marks.size) this.cvSuggested.set(left);
	}

	/** The exact body `PATCH /step/:step` expects, per ONBOARDING_STEP_SCHEMAS. */
	private stepPayload(step: OnboardingScreen): unknown {
		const blank = (value: string | null | undefined) => (value?.trim() ? value.trim() : null);
		switch (step) {
			case 'personal': {
				const { cityOther, ...v } = this.form.controls.personal.getRawValue();
				// Un input numérico vacío llega como '' o NaN; los dos tienen que salir
				// como null o el schema rechaza el paso entero por un campo opcional.
				const height = Number(v.heightCm);
				return {
					...v,
					// Fuera de la lista se guarda lo escrito (ciudad y país), no «Other».
					city: v.city === 'Other' ? (blank(cityOther) ?? 'Other') : v.city,
					nationality: this.nationalityToSend(v.countryCode ?? '', v.nationality ?? ''),
					countryCode: v.countryCode || null,
					linkedinUrl: withScheme(v.linkedinUrl),
					heightCm: Number.isFinite(height) && height > 0 ? height : null,
				};
			}
			case 'experience':
				return {
					entries: (this.experience.getRawValue() as ExperienceEntry[]).map((e) => ({
						...e,
						countryCode: e.countryCode || null,
						toDate: blank(e.toDate),
						description: blank(e.description),
					})),
					managementExperience: this.form.controls.experienceInfo.getRawValue().managementExperience,
				};
			case 'education':
				return {
					entries: (this.education.getRawValue() as EducationEntry[]).map((e) => ({
						...e,
						year: Number(e.year),
						isCertification: !!e.isCertification,
					})),
				};
			case 'languages':
				return {
					entries: (this.languages.getRawValue() as (Omit<LanguageEntry, 'cefr'> & { cefr: string })[]).map(
						(e) => {
							return {
								language: e.language,
								cefr: e.cefr,
								level: PROFICIENCY_OF_CEFR[e.cefr] ?? 'intermediate',
								percent:
									e.percent == null || (e.percent as unknown as string) === ''
										? null
										: Number(e.percent),
							};
						},
					),
				};
			case 'legal': {
				const v = this.form.controls.legal.getRawValue();
				return {
					visaStatus: v.visaStatus,
					emiratesId: blank(v.emiratesId),
					needsSponsor: !!v.needsSponsor,
					currentEmployment: blank(v.currentEmployment),
					drivingLicence: v.drivingLicence,
				};
			}
			case 'availability': {
				const v = this.form.controls.availability.getRawValue();
				const salary = v.salaryExpectationAed;
				const notice = v.noticePeriodDays;
				return {
					...v,
					salaryExpectationAed:
						salary == null || (salary as unknown as string) === '' ? null : Number(salary),
					noticePeriodDays: notice == null || (notice as unknown as string) === '' ? null : Number(notice),
				};
			}
			case 'preferences': {
				const v = this.form.controls.preferences.getRawValue();
				const years = v.yearsInTargetRole;
				return {
					...v,
					yearsInTargetRole: years == null || (years as unknown as string) === '' ? null : Number(years),
				};
			}
			default:
				return {};
		}
	}

	/** Adopts the server's answer; only the first load writes into the form. */
	private applyState(state: OnboardingStateDTO, prefill: boolean): void {
		this.state.set(state);
		this.reviewing.set(state.underReview === true);
		if (prefill) {
			this.prefill(state);
			this.rebuildOptions();
		}
	}

	private prefill(state: OnboardingStateDTO): void {
		const p = state.personal ?? {};
		// Lo que el borrador ya traía, para no reescribir el texto declarado si el
		// país no cambia (ver `nationalityToSend`).
		this.storedCountryCode = p.countryCode ?? null;
		this.form.controls.personal.patchValue({
			fullName: p.fullName ?? '',
			email: p.email ?? '',
			phone: p.phone ?? '',
			birthDate: p.birthDate ?? '',
			nationality: p.nationality ?? '',
			countryCode: p.countryCode ?? '',
			city: p.city ?? '',
			linkedinUrl: p.linkedinUrl ?? '',
			heightCm: p.heightCm ?? null,
		});

		this.experience.clear();
		for (const entry of state.experience ?? []) this.experience.push(this.experienceGroup(entry));
		this.form.controls.experienceInfo.patchValue({ managementExperience: state.managementExperience ?? '' });
		this.education.clear();
		for (const entry of state.education ?? []) this.education.push(this.educationGroup(entry));
		this.languages.clear();
		for (const entry of state.languages ?? []) this.languages.push(this.languageGroup(entry));

		const legal = state.legal ?? {};
		this.form.controls.legal.patchValue({
			visaStatus: legal.visaStatus ?? '',
			emiratesId: legal.emiratesId ?? '',
			needsSponsor: legal.needsSponsor ?? false,
			currentEmployment: legal.currentEmployment ?? '',
			drivingLicence: legal.drivingLicence ?? '',
		});

		const availability = state.availability ?? {};
		this.form.controls.availability.patchValue({
			availability: availability.availability ?? '',
			relocation: availability.relocation ?? '',
			gccExperience: availability.gccExperience ?? '',
			salaryExpectation: offeredSalaryExpectation(availability.salaryExpectation),
			salaryExpectationAed: availability.salaryExpectationAed ?? null,
			noticePeriodDays: availability.noticePeriodDays ?? null,
			locationTargeting: availability.locationTargeting ?? [],
		});

		const preferences = state.preferences ?? {};
		this.form.controls.preferences.patchValue({
			targetPositions: preferences.targetPositions ?? [],
			targetPositionOther: preferences.targetPositionOther ?? null,
			propertyTypes: preferences.propertyTypes ?? [],
			yearsInTargetRole: preferences.yearsInTargetRole ?? null,
			systems: preferences.systems ?? [],
			specializations: preferences.specializations ?? [],
			skills: preferences.skills ?? [],
		});
	}

	// ---- Uploads ------------------------------------------------------------

	onUpload(kind: OnboardingUploadKind, picked: AcceptedUpload): void {
		this.uploadError.set(null);
		this.uploadingKind.set(kind);
		// On the public signup the first file is what opens the draft.
		this.ensureStarted().subscribe((ok) => {
			if (ok) this.upload(kind, picked);
			else this.uploadingKind.set(null);
		});
	}

	private upload(kind: OnboardingUploadKind, picked: AcceptedUpload): void {
		this.gateway.upload(kind, picked.file, picked.durationSeconds).subscribe({
			next: ({ state, extractionId }) => {
				this.uploadingKind.set(null);
				this.applyState(state, false);
				if (kind === 'cv' && extractionId != null) this.watchCvExtraction(extractionId);
			},
			error: (err) => {
				this.uploadingKind.set(null);
				this.uploadError.set(
					this.apiError(
						err,
						err?.status === 413 ? 'merit.onboarding.errors.fileTooLarge' : 'merit.onboarding.errors.upload',
					),
				);
			},
		});
	}

	// ---- CV + IA (MD-Onboarding-Candidato-CV-IA) ----------------------------

	/** La lectura en curso o terminada del CV que se acaba de subir. */
	readonly cvExtraction = signal<CvExtractionDTO | null>(null);
	/** "Analizando tu CV…" — la IA está leyendo. */
	readonly cvAnalyzing = signal(false);
	/** Rutas de campos que vienen de la IA y el candidato aún no ha confirmado. */
	readonly cvSuggested = signal<ReadonlySet<string>>(new Set());
	private cvPoll?: ReturnType<typeof setTimeout>;

	/** Un campo marcado como "sugerido del CV" hasta que se guarda su paso. */
	isSuggested(path: string): boolean {
		return this.cvSuggested().has(path);
	}

	/**
	 * Sigue la lectura hasta que termina.
	 *
	 * Se pregunta en vez de esperar en la subida porque leer un CV tarda entre 5
	 * y 30 segundos, y dejar la pantalla bloqueada todo ese rato es la forma
	 * segura de que la persona cierre la pestaña. El tope corta a los 2 minutos:
	 * si el proveedor se queda colgado, el candidato sigue a mano — la IA nunca
	 * bloquea el onboarding (§4).
	 */
	private watchCvExtraction(id: number, attempt = 0): void {
		// Subir un segundo CV arrancaba una cadena nueva sin matar la anterior, y
		// `cvPoll` solo guarda el último handle: la primera seguía preguntando.
		if (this.cvPoll) clearTimeout(this.cvPoll);
		this.cvAnalyzing.set(true);
		// `takeUntilDestroyed` porque `clearTimeout` en ngOnDestroy NO cancela el
		// GET en vuelo: si la respuesta llegaba después de destruir el componente,
		// su `next` programaba un timeout nuevo que ya nadie limpiaría, y la
		// cadena se reencolaba hasta 40 veces cada 3 segundos.
		this.gateway
			.cvExtraction(id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (extraction) => {
					this.cvExtraction.set(extraction);
					if (extraction.status === 'processing') {
						if (attempt >= 40) {
							this.cvAnalyzing.set(false);
							return;
						}
						this.cvPoll = setTimeout(() => this.watchCvExtraction(id, attempt + 1), 3000);
						return;
					}
					this.cvAnalyzing.set(false);
					if (extraction.status === 'ok' || extraction.status === 'partial') this.applyCvPrefill(extraction);
				},
				error: () => {
					this.cvAnalyzing.set(false);
					this.cvExtraction.set(null);
				},
			});
	}

	/**
	 * Vuelca la propuesta de la IA en el formulario.
	 *
	 * **Solo rellena lo que está vacío.** Quien ya escribió su teléfono o corrigió
	 * un puesto no puede ver cómo se lo pisa una segunda subida de CV: esa es la
	 * política de reintento del §4 ("no pisar campos ya verificados").
	 */
	/**
	 * On opening the wizard, the CV proposal again — for the steps not saved yet
	 * (23/09). It only ever lived in the browser, so a reload or coming back with
	 * the link days later lost it, and the person faced empty steps for a CV we
	 * had already read. A saved step keeps exactly what was saved.
	 */
	private reapplyCvProposal(state: OnboardingStateDTO): void {
		const id = state.cvExtractionId;
		if (id == null) return;
		const saved = new Set<string>(state.completedSteps);
		this.gateway
			.cvExtraction(id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (extraction) => {
					if (extraction.status === 'ok' || extraction.status === 'partial') {
						this.applyCvPrefill(extraction, saved);
					}
				},
				error: () => undefined,
			});
	}

	private applyCvPrefill(extraction: CvExtractionDTO, saved: ReadonlySet<string> = new Set()): void {
		const payload = extraction.payload;
		if (!payload) return;
		const marked = new Set<string>();
		// The storage step a form path belongs to; `experienceInfo` is saved with
		// `experience`.
		const isSaved = (path: string) => saved.has(path.split('.')[0].replace('experienceInfo', 'experience'));

		const fill = (path: string, value: unknown): void => {
			if (value == null || value === '' || isSaved(path)) return;
			const control = this.form.get(path);
			const current = control?.value;
			if (!control || (current !== null && current !== undefined && current !== '')) return;
			control.setValue(value);
			marked.add(path);
		};

		const personal = payload.personal ?? {};
		fill('personal.fullName', personal.fullName);
		fill('personal.email', personal.email);
		fill('personal.phone', personal.phone);
		fill('personal.birthDate', personal.birthDate);
		fill('personal.nationality', personal.nationality);
		// El desplegable que se ve es el país, no el texto: sin esto la nacionalidad
		// que el CV trae se quedaría en un campo que ya nadie mira.
		fill('personal.countryCode', OnboardingComponent.countryCodeOf(personal.nationality));
		fill('personal.city', personal.city);
		fill('personal.linkedinUrl', personal.linkedinUrl);
		// Extracted all along and dropped here until 23/09.
		fill('personal.heightCm', personal.heightCm);

		// Las listas solo se proponen si el candidato no ha metido nada: mezclar su
		// experiencia con la del CV crearía duplicados que tendría que ir borrando.
		if (this.experience.length === 0 && !saved.has('experience')) {
			for (const entry of payload.experience ?? []) {
				this.experience.push(
					this.experienceGroup({
						position: entry.position ?? '',
						department: entry.department ?? '',
						employerProperty: entry.employerProperty ?? '',
						luxuryLevel: entry.luxuryLevel ?? '',
						countryCode: OnboardingComponent.jobCountryOf(entry.countryCode),
						fromDate: entry.fromDate ?? '',
						toDate: entry.toDate ?? null,
						description: entry.description ?? '',
					} as ExperienceEntry),
				);
				marked.add(`experience.${this.experience.length - 1}`);
			}
		}
		if (this.education.length === 0 && !saved.has('education')) {
			for (const entry of payload.education ?? []) {
				this.education.push(
					this.educationGroup({
						level: entry.level ?? '',
						title: entry.title ?? '',
						institution: entry.institution ?? '',
						year: entry.year ?? null,
						isCertification: entry.isCertification ?? false,
					} as unknown as EducationEntry),
				);
				marked.add(`education.${this.education.length - 1}`);
			}
		}
		if (this.languages.length === 0 && !saved.has('languages')) {
			for (const entry of payload.languages ?? []) {
				// The CEFR the CV gave; without one, the lower CEFR of its band —
				// a conservative suggestion the person corrects, never a gift.
				const cefr =
					entry.cefr ??
					({ basic: 'A2', intermediate: 'B1', fluent: 'C1', native: 'C2' } as const)[entry.level] ??
					null;
				this.languages.push(
					this.languageGroup({ language: entry.language, level: entry.level, cefr, percent: null }),
				);
				marked.add(`languages.${this.languages.length - 1}`);
			}
		}

		fill('experienceInfo.managementExperience', payload.managementExperience);
		fill('legal.visaStatus', payload.legal?.visaStatus);
		fill('legal.currentEmployment', payload.legal?.currentEmployment);
		fill('legal.drivingLicence', payload.legal?.drivingLicence);
		fill('availability.availability', payload.availability?.availability);
		fill('availability.gccExperience', payload.availability?.gccExperience);
		fill('availability.salaryExpectation', offeredSalaryExpectation(payload.availability?.salaryExpectation));
		fill('availability.noticePeriodDays', payload.availability?.noticePeriodDays);

		const targetPositions = payload.preferences?.targetPositions ?? [];
		if (
			targetPositions.length &&
			!saved.has('preferences') &&
			!this.form.controls.preferences.controls.targetPositions.value?.length
		) {
			this.form.controls.preferences.controls.targetPositions.setValue([...targetPositions]);
			marked.add('preferences.targetPositions');
		}
		const systems = payload.preferences?.systems ?? [];
		if (
			systems.length &&
			!saved.has('preferences') &&
			!this.form.controls.preferences.controls.systems.value?.length
		) {
			this.form.controls.preferences.controls.systems.setValue([...systems]);
			marked.add('preferences.systems');
		}
		fill('preferences.yearsInTargetRole', payload.preferences?.yearsInTargetRole);
		const skills = payload.preferences?.skills ?? [];
		if (
			skills.length &&
			!saved.has('preferences') &&
			!this.form.controls.preferences.controls.skills.value?.length
		) {
			this.form.controls.preferences.controls.skills.setValue([...skills]);
			marked.add('preferences.skills');
		}

		this.cvSuggested.set(marked);
		// Un valor del CV puede ser un valor de catálogo desactivado o renombrado;
		// sin esto el desplegable se quedaría en blanco con el dato dentro.
		this.rebuildOptions();
		// Deja constancia de que el candidato ha recibido la propuesta.
		this.gateway.applyCvExtraction(extraction.id).subscribe({ error: () => undefined });
	}

	// ---- Submit -------------------------------------------------------------

	submit(): void {
		this.submitError.set(null);
		this.submitting.set(true);
		this.gateway.submit().subscribe({
			next: (response) => {
				this.submitting.set(false);
				// `/me` is cached for the session and the submit is what lifts the
				// "complete your profile" limit (10/09): without this, the guard
				// would keep bouncing a migrated candidate back here after finishing.
				this.portal.invalidateCandidateMe();
				this.unlocked.set(response.accessUnlocked === true);
				this.reviewing.set(response.underReview === true);
				this.view.set('done');
				if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
			},
			error: (err) => {
				this.submitting.set(false);
				this.submitError.set(this.apiError(err, 'merit.onboarding.errors.submit'));
			},
		});
	}

	// ---- Helpers ------------------------------------------------------------

	private t(key: string, params?: Record<string, unknown>): string {
		return this.transloco.translate(key, params);
	}

	private apiError(err: unknown, fallbackKey: string): string {
		const video = videoDurationErrorMessage(err, (key, params) => this.t(key, params));
		if (video) return video;
		const message = (err as { error?: { message?: unknown } })?.error?.message;
		if (message === 'PROFILE_UNDER_REVIEW') return this.t('merit.onboarding.errors.underReview');
		return typeof message === 'string' && message ? message : this.t(fallbackKey);
	}

	/** True once the candidate tried to move on and the control is still invalid. */
	showErr(path: string): boolean {
		const control = this.form.get(path);
		return !!control && control.invalid && this.triedNext();
	}

	/**
	 * El mensaje de un campo, según el error que tenga (03/09 · 39): elegir un
	 * cuarto puesto decía «Este campo es obligatorio» porque el control solo
	 * sabía pintar el genérico.
	 */
	errMsg(path: string): string | undefined {
		const control = this.form.get(path);
		if (!control?.errors) return undefined;
		if (control.errors['maxlength']) {
			return this.transloco.translate('merit.onboarding.errors.maxPositions', {
				max: control.errors['maxlength'].requiredLength,
			});
		}
		return undefined;
	}
}
